import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';

import { getActiveProduct } from '../../../store/products/selectors';
import { selectDraftScopeChrome } from '../../../store/selectors/draftScopeChromeSelectors';
import {
  selectEffectiveChannelState,
  selectEffectiveIsMasterView,
  selectProductBusyLoadingText,
  selectProductIsBusy,
} from '../../../store/selectors/productSelectors';
import l10n from '../../../shared/strings';
import ButtonLoader from '../ConnectChannelSection/ButtonLoader';
import { productStateTabConstants } from '../constants/productStateTabConstants';
import styles from './styles.module.scss';

const copy = l10n.PRODUCT_INFO.draftScope;

const ProductStateTabsBar = ({ productId, variantId: variantIdProp, onEditText }) => {
  const location = useLocation();
  const variantId = variantIdProp ?? getActiveProduct(location)?.variantId;
  const {
    activeTab,
    contextChannelOption,
    hasStaging,
    hasLive,
  } = useSelector((state) => selectEffectiveChannelState(state, productId, variantId));
  const isMasterView = useSelector((state) => selectEffectiveIsMasterView(state, productId, variantId));
  const isBusy = useSelector((state) => selectProductIsBusy(state, productId));
  const busyLoadingText = useSelector((state) => selectProductBusyLoadingText(state, productId));
  const draftScopeChrome = useSelector((state) => (
    selectDraftScopeChrome(state, productId, variantId)
  ));
  const showHeaderTextAction = Boolean(
    onEditText
    && !isBusy
    && activeTab === productStateTabConstants.DRAFT
    && draftScopeChrome
    && !draftScopeChrome.hideDetails
  );

  const tabsList = useMemo(() => ([
    {
      id: productStateTabConstants.DRAFT,
      text: 'Draft',
    },
    {
      id: productStateTabConstants.PUBLISHED,
      text: 'Published',
    },
  ]), []);

  if (isMasterView || (!hasStaging && !hasLive)) return null;

  return (
    <div className={styles.actionsRow}>
      <If condition={(hasStaging || hasLive)}>
        {tabsList.map((tab) => {
          const isLiveActive = (tab.id === productStateTabConstants.PUBLISHED && hasLive);
          const isStagingActive = (tab.id === productStateTabConstants.DRAFT && hasStaging);
          if (!isLiveActive && !isStagingActive) return null;
          if (hasStaging && hasLive && tab.id !== activeTab) return null;
          return (
            <div key={tab.id} className={cn(styles.stateLabel, styles.existILife)}>
              <span>{tab.text}</span>
              <span className={cn(styles.dot, styles[`${tab.id}Dot`])} />
              <If condition={contextChannelOption?.label}>
                <span className={styles.channelLabel}>
                  ({' '}
                  <span className={styles.channelLabelText}>{contextChannelOption.label}</span>
                  {' '})
                </span>
              </If>
            </div>
          );
        })}
      </If>
      <If condition={showHeaderTextAction}>
        <span className={styles.actionsMainWrapper}>
          <button
            type="button"
            className={styles.primaryAction}
            data-testid="draft-scope-edit-text-header"
            onClick={onEditText}
          >
            {hasLive ? copy.manageDraftText : copy.updateFromMaster}
          </button>
        </span>
      </If>
      <If condition={isBusy}>
        <span className={styles.actionsMainWrapper}>
          <span className={styles.actionsMain}>
            <span className={styles.busyStatus}>
              <ButtonLoader />
              <If condition={busyLoadingText}>
                <span>{busyLoadingText}</span>
              </If>
            </span>
          </span>
        </span>
      </If>
    </div>
  );
};

ProductStateTabsBar.propTypes = {
  productId: PropTypes.string.isRequired,
  variantId: PropTypes.string,
  onEditText: PropTypes.func,
};

ProductStateTabsBar.defaultProps = {
  variantId: null,
  onEditText: undefined,
};

export default ProductStateTabsBar;
